import { useRef } from "react"
import { Info } from "luxon"
import { Select, FormLabel } from "../../"
import { CalendarState } from "react-stately"
import { DatePickerCalendarDay } from "./DatePickerCalendarDay"
import { useCalendar, useCalendarGrid } from "react-aria"
import { getYearRange } from "./utils"
import { endOfMonth, CalendarDate, getWeeksInMonth } from "@internationalized/date"
import { ReactAriaCalendarProps } from "./types"

interface DatePickerCalendarProps {
  locale: string
  timeZone: string
  minSelectableYear: number
  maxSelectableYear: number
  calendarState: CalendarState
  ariaCalendarProps: ReactAriaCalendarProps
  monthSelectLabelText: string
  yearSelectLabelText: string
}

export function DatePickerCalendar({
  locale,
  timeZone,
  minSelectableYear,
  maxSelectableYear,
  monthSelectLabelText,
  yearSelectLabelText,
  calendarState,
  ariaCalendarProps,
}: DatePickerCalendarProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  const { calendarProps: calendarContainerProps } = useCalendar(ariaCalendarProps, calendarState)

  const displayedMonthStartDate = calendarState.visibleRange.start.add({})
  const displayedMonthEndDate = endOfMonth(displayedMonthStartDate)
  const weeksInMonth = getWeeksInMonth(displayedMonthStartDate, locale)

  const weekDays = Info.weekdays("long", { locale })
  const { gridProps, headerProps } = useCalendarGrid(
    {
      startDate: displayedMonthStartDate,
      endDate: displayedMonthEndDate,
    },
    calendarState,
  )

  function handleMonthChange(month: string) {
    const { day, year } = calendarState.visibleRange.start
    calendarState.setFocusedDate(new CalendarDate(year, Number(month), day))
  }

  function handleYearChange(year: string) {
    const { day, month } = calendarState.visibleRange.start
    calendarState.setFocusedDate(new CalendarDate(Number(year), month, day))
  }

  return (
    <div
      ref={containerRef}
      {...calendarContainerProps}
      className="inline-block rounded-lg border border-gray-200 bg-white px-2 py-3 shadow-md"
    >
      <div className="mb-2 flex gap-x-2 p-1">
        <Select
          size="sm"
          name="month"
          label={<FormLabel label={monthSelectLabelText} className="sr-only" />}
          value={String(calendarState.visibleRange.start.month)}
          onChange={value => handleMonthChange(value as string)}
        >
          {Info.months("long", { locale }).map((monthName, monthIndex) => (
            <Select.Option key={monthName} value={String(monthIndex + 1)}>
              {monthName}
            </Select.Option>
          ))}
        </Select>

        <Select
          size="sm"
          name="year"
          label={<FormLabel label={yearSelectLabelText} className="sr-only" />}
          value={String(calendarState.visibleRange.start.year)}
          onChange={value => handleYearChange(value as string)}
        >
          {getYearRange(minSelectableYear, maxSelectableYear).map(year => (
            <Select.Option key={year} value={String(year)}>
              {year}
            </Select.Option>
          ))}
        </Select>
      </div>

      <table {...gridProps} cellPadding={0} className="table-fixed">
        <thead {...headerProps}>
          <tr>
            {weekDays.map((longDayName, index) => (
              <th
                key={index}
                scope="col"
                abbr={longDayName}
                className="h-8 w-9 justify-center py-2 text-xs font-medium leading-4 tracking-sm text-gray-400"
              >
                {longDayName.substring(0, 3)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[...new Array(weeksInMonth).keys()].map(weekIndex => (
            <tr key={weekIndex}>
              {calendarState
                .getDatesInWeek(weekIndex, displayedMonthStartDate)
                .map((date, dateIndex) =>
                  date ? (
                    <DatePickerCalendarDay
                      key={dateIndex}
                      date={date}
                      timeZone={timeZone}
                      calendarState={calendarState}
                      startOfCurrentMonth={displayedMonthStartDate}
                    />
                  ) : (
                    <td key={dateIndex} className="h-8"></td>
                  ),
                )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
